import { useEffect, useMemo, useRef, useState } from "react";
import { DEFAULT_LIMIT_ITEMS } from "constants/data";
import { PaginationParamType } from "type";
import { PRICE_SETTING_TIRE } from "features/proc_fee/types/tire";
import {
  fetchSelectWinterWheelPrice,
  fetchWinterWheelPrice,
  selectWinterWheelPriceMethod,
  updateWinterWheelPrice,
  updateStorePriceSettingApi,
} from "features/mst_wheel/api/wheelPriceApi";
import {
  SelectWinterWheelPriceRequest,
  WinterWheelPriceItem,
  WinterWheelSellingItem,
} from "features/mst_wheel/types/winter-wheel-price";
import { StorePriceSettingWinterWheelParams } from "features/mst_wheel/types/wheel-price";
import Notify from "config/notify";

/**
 * useWinterWheelPriceSetting
 *
 * Custom React hook for managing the state and actions related to winter wheel price settings.
 * Handles fetching, updating, and selecting winter wheel price data, as well as pagination and modal state.
 *
 * @returns {Object} State and action handlers for winter wheel price management:
 *   - winterWheelPriceList: List of winter wheel price items.
 *   - selectedWinterWheelSelling: List of selected winter wheel selling items.
 *   - openWinterWheelSellingModal: Boolean indicating if the selling modal is open.
 *   - winterWheelPriceMethod: Current price method.
 *   - setWinterWheelPriceMethod: Setter for price method.
 *   - handleSelectWinterWheel: Handler to select a winter wheel and open the modal.
 *   - handleClearSelectedWinterWheel: Handler to clear selected selling items and close the modal.
 *   - handleUpdateWinterWheelPrice: Handler to update a winter wheel price.
 *   - onLoadMore: Handler for loading more data (pagination).
 *   - loading: Boolean indicating loading state.
 */
const useWinterWheelPriceSetting = ({ isProductRole }: { isProductRole: boolean }) => {
  const [winterWheelPriceList, setWinterWheelPriceList] = useState<WinterWheelPriceItem[]>([]);
  const [selectedWinterWheelSelling, setSelectedWinterWheelSelling] = useState<
    WinterWheelSellingItem[]
  >([]);
  const [page, setPage] = useState(1);
  const [openWinterWheelSellingModal, setOpenWinterWheelSellingModal] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [winterWheelPriceMethod, setWinterWheelPriceMethod] = useState<number>();
  const [updateKey, setUpdateKey] = useState(0);
  /** Only call mst/store/update when the user explicitly changes the dropdown */
  const isUserChange = useRef(false);
  /** True when user just changed the method dropdown so we refetch from offset 1 */
  const userChangedMethodRef = useRef(false);

  const storeCd = localStorage.getItem("shop_cd") || "";

  const totalPage = useMemo(() => Math.ceil(total / DEFAULT_LIMIT_ITEMS), [total]);
  const hasMore = useMemo(() => page < totalPage, [page, totalPage]);

  const fetchData = async (isLoadMore = false, explicitOffset?: number) => {
    const offset = explicitOffset !== undefined ? explicitOffset : page;
    const prevScrollY =
      isLoadMore ? document.documentElement.scrollTop || document.body.scrollTop : 0;
    setLoading(true);
    try {
      const queryParams: PaginationParamType = {
        limit: DEFAULT_LIMIT_ITEMS,
        offset,
      };
      const data = await fetchWinterWheelPrice(queryParams);
      if (data) {
        if (isLoadMore) {
          // When loading more, append new data to existing list
          setWinterWheelPriceList((prev) => [...prev, ...data.list]);
        } else {
          // When loading first time, replace entire list
          setWinterWheelPriceList(data.list);
        }
        setTotal(data.total);
      }
      if (isLoadMore && prevScrollY > 0) {
        requestAnimationFrame(() => {
          window.scrollTo(0, prevScrollY);
        });
      }
    } catch (error) {
      console.error("Error fetching winter wheel price data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateWinterWheelPrice = async (
    id: number,
    key: keyof WinterWheelPriceItem,
    data: number | string,
  ) => {
    const changedIndex = winterWheelPriceList.findIndex((item) => item.id === id);
    const item = winterWheelPriceList[changedIndex];

    if (changedIndex === -1 || !item) return;

    const originalValue = (item as unknown as Record<string, unknown>)[key];
    const isCleared =
      data === "" &&
      originalValue !== "" &&
      originalValue !== null &&
      originalValue !== undefined;

    // For HQ users on /mst-wheel-price/winter-setting, blurring a cleared cell that
    // originally had a value is not allowed: show an error dialog and restore the previous
    // value instead of sending column_delete_flag. Originally-empty cells fall through to
    // the existing "no diff" early return below, so no dialog is shown for a no-op blur.
    if (isProductRole && isCleared) {
      setWinterWheelPriceList((prev) => {
        const newList = [...prev];
        if (newList[changedIndex]) {
          newList[changedIndex] = {
            ...newList[changedIndex],
            [key]: originalValue,
          } as WinterWheelPriceItem;
        }
        return newList;
      });
      setUpdateKey((prev) => prev + 1);
      Notify.setNotification({
        show: true,
        title: "値を入力してください",
        confirmLabel: "閉じる",
        actionsPosition: "center",
      });
      return;
    }

    const isOriginallyEmpty =
      originalValue === "" || originalValue === null || originalValue === undefined;
    const isDataEmpty = data === "" || data === null || data === undefined;

    // No-op cases: originally empty + still empty, or value unchanged.
    if (!isCleared) {
      if (isOriginallyEmpty && isDataEmpty) return;
      if (
        !isOriginallyEmpty &&
        item[key] !== undefined &&
        item[key] !== null &&
        String(item[key]) === String(data)
      ) {
        return;
      }
    }

    try {
      await updateWinterWheelPrice({
        wheel_rim_diam: item.wheel_rim_diam,
        wheel_width: item.wheel_width,
        wheel_pcd: item.wheel_pcd,
        wheel_selling_price: isCleared ? 0 : (data as number),
        ...(isCleared && { column_delete_flag: 1 }),
      });

      await fetchData(false, 1);
      setUpdateKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error updating winter wheel price:", error);
    }
  };

  const fetchSelectedWheelPrice = async (item?: WinterWheelPriceItem) => {
    const requestBody: SelectWinterWheelPriceRequest | {} = item
      ? {
          wheel_rim_diam: item.wheel_rim_diam,
          wheel_width: item.wheel_width,
          wheel_pcd: item.wheel_pcd,
        }
      : {};
    try {
      const res = await fetchSelectWinterWheelPrice(requestBody);
      if (res?.list && res?.list.length > 0) {
        setSelectedWinterWheelSelling(res.list);
      } else {
        setSelectedWinterWheelSelling([]);
      }
    } catch (error) {
      console.error("Error fetching selected wheel price:", error);
    }
  };

  const handleSelectWinterWheel = async (item?: WinterWheelPriceItem) => {
    await fetchSelectedWheelPrice(item);
    setOpenWinterWheelSellingModal(true);
  };

  const handleClearSelectedWinterWheel = () => {
    setSelectedWinterWheelSelling([]);
    setOpenWinterWheelSellingModal(false);
  };

  const onLoadMore = async () => {
    if (!hasMore || loading) {
      return;
    }
    setPage((prevPage) => prevPage + 1);
  };

  // Reset pagination when method changes. Only clear list when switching to COST (list is hidden in COST mode).
  const handleMethodChange = (method: number) => {
    setWinterWheelPriceMethod(method);
  };

  /** Call API update only when user explicitly changes 廉価 ホイールセット時の売価計算方法 dropdown */
  const handleWinterWheelPriceMethodChange = (method: number) => {
    isUserChange.current = true;
    userChangedMethodRef.current = true;
    setPage(1);
    setWinterWheelPriceList([]);
    setTotal(0);
    handleMethodChange(method);
  };

  const updateStorePriceSetting = async () => {
    const updateData: StorePriceSettingWinterWheelParams = {
      store_cd: storeCd,
      mst_store_price_setting_wheel_low_price: winterWheelPriceMethod || 0,
    };
    await updateStorePriceSettingApi(updateData);
  };

  // Only call mst/store/update when the user explicitly changes the dropdown (not on load/reload)
  useEffect(() => {
    if (!isUserChange.current) return;
    isUserChange.current = false;
    if (storeCd && winterWheelPriceMethod !== undefined) updateStorePriceSetting();
  }, [winterWheelPriceMethod]);

  // When user changed the method dropdown, refetch list from offset 1 (only for SELLING / product role)
  useEffect(() => {
    if (
      !userChangedMethodRef.current ||
      winterWheelPriceMethod === undefined ||
      (!isProductRole && winterWheelPriceMethod !== PRICE_SETTING_TIRE.SELLING)
    ) {
      return;
    }
    userChangedMethodRef.current = false;
    fetchData(false, 1);
  }, [winterWheelPriceMethod, isProductRole]);

  // Load more when page > 1 and method is SELLING
  useEffect(() => {
    if (page > 1 && page <= totalPage && ((!isProductRole && winterWheelPriceMethod === PRICE_SETTING_TIRE.SELLING) || isProductRole)) {
      fetchData(true);
    }
  }, [page, totalPage, isProductRole, winterWheelPriceMethod]);

  // Load method on first render; list is loaded by effect above when method === SELLING
  useEffect(() => {
    const getPriceSettingWheelMethod = async () => {
      const { list } = await selectWinterWheelPriceMethod({ store_cd: storeCd });

      if (list.length > 0) {
        setWinterWheelPriceMethod(list[0].mst_store_price_setting_wheel_low_price);
        return;
      }
      setWinterWheelPriceMethod(PRICE_SETTING_TIRE.SELLING);
    };

    if (storeCd) getPriceSettingWheelMethod();
    fetchData(false);
  }, []);

  return {
    // state
    winterWheelPriceList,
    selectedWinterWheelSelling,
    openWinterWheelSellingModal,
    winterWheelPriceMethod,
    updateKey,

    // actions
    setWinterWheelPriceMethod: handleWinterWheelPriceMethodChange,
    handleSelectWinterWheel,
    handleClearSelectedWinterWheel,
    handleUpdateWinterWheelPrice,
    onLoadMore,
    loading,
  };
};

export default useWinterWheelPriceSetting;
